import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

/*
 * Full-page order detail — the page version of what used to be the Live View (AllOrders.js) slide-in
 * drawer. Same four sections (Timeline, POD / Images, Duties & Tax, COD Custody) plus an order-summary
 * header, laid out with room to breathe.
 *
 * Reached from Live View via navigate('/orders/order-details-view?awb=…&orderId=…', { state:{ row } }).
 * The Live View row arrives in navigation state and is used as an instant first paint, but the page
 * no longer DEPENDS on it: the detail is fetched from getJobById and multipleAwbStatuses, so a hard
 * refresh or a pasted link renders the same page (it previously rendered no order context at all).
 */

const STATUS_META = {
  ALL: { label: 'All', color: '#6366f1', bg: '#eef2ff', icon: 'fa-th-large' },
  ASSIGNED: { label: 'Assigned', color: '#f59e0b', bg: '#fef3c7', icon: 'fa-user' },
  DELIVERED: { label: 'Delivered', color: '#10b981', bg: '#d1fae5', icon: 'fa-check-circle' },
  PLANNED: { label: 'Planned', color: '#3b82f6', bg: '#dbeafe', icon: 'fa-calendar' },
  PICKED_UP: { label: 'Picked Up', color: '#8b5cf6', bg: '#ede9fe', icon: 'fa-inbox' },
  PICKUP_ENROUTE: { label: 'Pickup Enroute', color: '#06b6d4', bg: '#cffafe', icon: 'fa-truck' },
  PICKUP_REQUESTED: { label: 'Pickup Req.', color: '#0891b2', bg: '#cffafe', icon: 'fa-hand-paper-o' },
  DROP_ENROUTE: { label: 'Drop Enroute', color: '#0ea5e9', bg: '#e0f2fe', icon: 'fa-road' },
  FAILED: { label: 'Failed', color: '#ef4444', bg: '#fee2e2', icon: 'fa-times-circle' },
  PENDING: { label: 'Pending', color: '#f97316', bg: '#ffedd5', icon: 'fa-clock-o' },
  UNASSIGNED: { label: 'Unassigned', color: '#64748b', bg: '#f1f5f9', icon: 'fa-question-circle' },
  INSCAN_AT_WAREHOUSE: { label: 'In Warehouse', color: '#7c3aed', bg: '#f3e8ff', icon: 'fa-archive' },
  DESTINATION_LINEHAUL_DEPART: { label: 'Linehaul', color: '#0d9488', bg: '#ccfbf1', icon: 'fa-plane' },
  READY: { label: 'Ready', color: '#2563eb', bg: '#dbeafe', icon: 'fa-flag' },
  RETURN_TO_SHIPPER: { label: 'Return', color: '#dc2626', bg: '#fee2e2', icon: 'fa-undo' },
  RTS: { label: 'RTS', color: '#dc2626', bg: '#fee2e2', icon: 'fa-undo' },
  RETURNED: { label: 'Returned', color: '#b91c1c', bg: '#fee2e2', icon: 'fa-reply' },
  CANCELLED: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', icon: 'fa-ban' },
};
const statusMeta = (s) =>
  STATUS_META[(s || '').toUpperCase()] || { label: s || 'N/A', color: '#94a3b8', bg: '#f1f5f9', icon: 'fa-circle' };

const TABS = [
  ['timeline', 'fa-list-ul', 'Timeline'],
  ['pod', 'fa-image', 'POD / Images'],
  ['duties', 'fa-file-text-o', 'Duties & Tax'],
  ['custody', 'fa-exchange', 'COD Custody'],
];

export default function LiveOrderDetail() {
  const API_URL = process.env.REACT_APP_API_URL;
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const awb = query.get('awb') || location.state?.row?.awb || '';
  const orderId =
    query.get('orderId') || location.state?.row?.order_id || location.state?.row?.orderId || '';
  // Live-view row for the summary header (present when navigated from Live View; absent on a hard refresh).
  const row = location.state?.row || null;
  // The full order record from /jobs/getJobById.
  //
  // This page used to render its summary ONLY from location.state.row - the row Live View hands
  // over on navigation - so a hard refresh or a shared/pasted link (which carry no navigation
  // state) produced a page with four tabs and no order context at all. Meanwhile the getJobById
  // response, already being fetched here, was thrown away except for its POD image URLs, even
  // though it carries ~40 fields including the line items and child AWBs. It is kept now, and the
  // summary prefers it, so a deep link shows the same detail as an in-app click.
  const [job, setJob] = useState(null);
  // Both ends of the shipment. getJobById returns ONE LEG only - for a DROP leg that is the
  // delivery address, with no trace of where the parcel is collected. /jobs/multipleAwbStatuses
  // already resolves the sibling PICK_UP leg server-side and returns shipperDetails alongside
  // consigneeDetails, so one extra call gives this page the FROM as well as the TO.
  const [ends, setEnds] = useState(null);
  // Forwarding — whether this leg was handed to an external carrier, and to whom.
  //
  // The record lives in the MAIN backend (forwarded_shipment); the driver app reads it through its
  // own read-only proxy at /api/forwarded-orders. That proxy is currently refused by the main
  // backend's network gate, so the lookup is written to fail SOFTLY: an order that cannot be
  // checked says so in one quiet line instead of throwing an error banner over a page that is
  // otherwise complete.
  const [fwd, setFwd] = useState(null);
  const [fwdError, setFwdError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [podImages, setPodImages] = useState([]);
  const [podSignatures, setPodSignatures] = useState([]);
  const [duties, setDuties] = useState([]);
  const [custody, setCustody] = useState(null);

  const driverName = (id) => {
    const d = drivers.find((dr) => dr.driver_id === parseInt(id));
    return d ? `${id} - ${d.driver_name}` : id || '';
  };

  // Driver list — resolves the numeric driverId on timeline events into a readable name.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}shypvdriverapp/driver/getAllDrivers`)
      .then((res) => {
        if (!cancelled && res.data?.success) {
          setDrivers(res.data.data.map((d) => ({ driver_id: d.driverId, driver_name: d.name })));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [API_URL]);

  // The four detail sources, keyed by AWB (+ orderId for POD) — the same calls the old drawer made.
  useEffect(() => {
    let cancelled = false;
    if (!awb) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setTimeline([]);
    setPodImages([]);
    setPodSignatures([]);
    setDuties([]);
    setCustody(null);
    setJob(null);
    setEnds(null);
    setFwd(null);
    setFwdError(null);

    const jobs = [
      axios
        .post(`${API_URL}shypvdriverapp/jobs/getJobDetails`, { awbNo: awb })
        .then((res) => {
          if (!cancelled && res.data?.success) setTimeline(res.data.data || []);
        })
        .catch(() => {}),
      orderId
        ? axios
            .get(`${API_URL}shypvdriverapp/jobs/getJobById`, { params: { id: orderId } })
            .then((res) => {
              if (cancelled) return;
              const record = res.data?.data?.[0] || null;
              setJob(record);
              const pod = record?.preSignedUrlsByType || {};
              setPodImages(Object.values(pod.Attachments || {}));
              setPodSignatures(Object.values(pod.Signatures || {}));
            })
            .catch(() => {})
        : Promise.resolve(),
      axios
        .get(`${API_URL}shypvdriverapp/api/forwarded-orders`, { params: { ref: awb, size: 5 } })
        .then((res) => {
          if (cancelled) return;
          const body = res.data;
          if (body && body.success === false) {
            const first = (body.errors || [])[0];
            setFwdError(first?.errorDetails || first?.errorMessage || 'Forwarding status unavailable');
            return;
          }
          // The scope paginates as { items, page, size, total, viewer } - not Spring's `content`.
          const list = Array.isArray(body?.data) ? body.data : (body?.data?.items || []);
          // Match the leg exactly - a "contains" search can return a sibling leg or a similar AWB.
          const hit = list.find((f) => (f.legBookingId || f.awb || '') === awb) || list[0] || null;
          setFwd(hit);
        })
        .catch((e) => {
          if (!cancelled) setFwdError(e?.message || 'Forwarding status unavailable');
        }),
      axios
        .post(`${API_URL}shypvdriverapp/jobs/multipleAwbStatuses`, [awb])
        .then((res) => {
          if (!cancelled) setEnds((res.data?.data || [])[0] || null);
        })
        .catch(() => {}),
      axios
        .get(`${API_URL}shypvdriverapp/duties-tax/byAwb`, { params: { awb } })
        .then((res) => {
          if (!cancelled) setDuties((res.data && res.data.data) || []);
        })
        .catch(() => {}),
      axios
        .get(`${API_URL}shypvdriverapp/admin/wallet/custody/awb/${encodeURIComponent(awb)}/trail`)
        .then((res) => {
          if (!cancelled) setCustody((res.data && res.data.data) || null);
        })
        .catch(() => {}),
    ];

    Promise.allSettled(jobs).then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [API_URL, awb, orderId]);

  const back = () => {
    // Prefer the browser's own back so Live View keeps its filters/scroll; fall back to the list.
    if (window.history.length > 1) navigate(-1);
    else navigate('/orders/allorders');
  };

  const dutiesTotal = duties.reduce((s, d) => s + Number(d.amount || 0), 0);
  const headMeta = statusMeta(row?.type || row?.status || (timeline[0] && timeline[0].status));

  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };
  const fmtDay = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
  };
  const money = (v) => (v == null || v === '' ? '' : `${Number(v).toFixed(2)} AED`);

  /**
   * One readable address line.
   *
   * formattedAddress often ALREADY contains the city and state ("Al Barsha - Dubai - United Arab
   * Emirates"), and on thin records it IS just the city ("Abu Dhabi"). Listing Address, City and
   * State as three rows then printed the same word three times. Parts already present in the
   * address are dropped, so a rich address stays whole and a thin one does not repeat itself.
   */
  const composeAddress = (a) => {
    if (!a) return '';
    const base = (a.formattedAddress || '').trim();
    const extras = [a.city, a.state]
      .map((x) => (x || '').trim())
      .filter((x) => x && !base.toLowerCase().includes(x.toLowerCase()));
    const uniq = extras.filter((x, i) => extras.findIndex((y) => y.toLowerCase() === x.toLowerCase()) === i);
    return [base, ...uniq].filter(Boolean).join(', ');
  };

  /** A delivery window, or a single date when the two ends are the same (which is the common case). */
  const fmtSlot = (slot) => {
    if (!slot || (!slot.start && !slot.end)) return '';
    const a = slot.start ? new Date(slot.start) : null;
    const b = slot.end ? new Date(slot.end) : null;
    const same = a && b && a.getTime() === b.getTime();
    // Midnight-to-midnight is a date, not a time window - printing 00:00 twice reads as a bug.
    const midnight = (d) => d && d.getHours() === 0 && d.getMinutes() === 0;
    if (same || (!b && a)) return midnight(a) ? fmtDay(a) : fmtDate(a);
    if (a && b) {
      const sameDay = a.toDateString() === b.toDateString();
      if (midnight(a) && midnight(b)) return `${fmtDay(a)} — ${fmtDay(b)}`;
      return sameDay
        ? `${fmtDay(a)} ${a.toLocaleTimeString()} — ${b.toLocaleTimeString()}`
        : `${fmtDate(a)} — ${fmtDate(b)}`;
    }
    return fmtDate(a || b);
  };

  const pickup = ends?.shipperDetails || null;
  const dropoff = ends?.consigneeDetails || null;

  // FROM / TO — the two ends of the shipment, which is the first thing anyone opening this page
  // wants. getJobById alone cannot answer it for a DROP leg; multipleAwbStatuses resolves the
  // sibling PICK_UP leg, so the pickup side is real data, not the delivery address relabelled.
  const route = useMemo(() => {
    const legIsPickup = String(job?.type || '').toUpperCase() === 'PICK_UP';
    const legAddress = composeAddress(job?.locationAddress);
    const legContact = job?.contactPoint || {};
    return {
      from: {
        name: pickup?.name || (legIsPickup ? legContact.name : row?.shipper?.name) || '',
        phone: pickup?.phone || (legIsPickup ? legContact.number : row?.shipper?.phone) || '',
        address: pickup?.address || (legIsPickup ? legAddress : row?.shipper?.address) || '',
      },
      to: {
        name: dropoff?.name || (!legIsPickup ? legContact.name : row?.consignee?.name) || '',
        phone: dropoff?.phone || (!legIsPickup ? legContact.number : row?.consignee?.phone) || '',
        address: dropoff?.address || (!legIsPickup ? legAddress : row?.consignee?.address) || '',
      },
    };
  }, [job, pickup, dropoff, row]);

  // Grouped so the page can be read in sections instead of as one long column of labels.
  const groups = useMemo(() => {
    const g = [
      {
        title: 'Shipment',
        rows: [
          ['AWB', job?.jobId || awb],
          ['Client reference', job?.businessRefNo || row?.businessRefNo],
          ['Leg', job?.type],
          ['Shipment type', job?.shipmentType || row?.orderType],
          ['Service time', row?.serviceTime],
          ['Project', row?.projectName],
        ],
      },
      {
        title: 'Money',
        rows: [
          ['Payment type', job?.paymentType || row?.paymentType],
          ['COD to collect', money(job?.amountTransaction != null ? job.amountTransaction : row?.totalCod)],
          ['COD received', money(job?.cashAmount != null ? job.cashAmount : row?.codReceived)],
        ],
      },
      {
        title: 'Handling',
        rows: [
          ['Driver', (job?.driverId || row?.driverId) ? driverName(job?.driverId || row?.driverId) : ''],
          ['Partner', job?.partnerId],
          ['Team', job?.teamId],
          ['Homebase', job?.homebaseId],
          ['OTP', job?.otpCode],
          ['Handover technician', job?.handoverTechnicianName],
          ['Delivery issue', job?.deliveryStatusReason],
          ['Pickup issue', job?.pickupStatusReason],
        ],
      },
      {
        title: 'Timing',
        rows: [
          ['Order date', fmtDay(job?.orderDate || row?.date)],
          ['Slot', fmtSlot(job?.slot)],
          ['Assigned', fmtDate(job?.jobAssignedDate)],
          ['Scanned', fmtDate(job?.scanDateTime)],
          ['Created', fmtDate(job?.createdDate)],
          ['Last updated', fmtDate(job?.modifiedDate)],
        ],
      },
    ];
    return g
      .map((x) => ({ ...x, rows: x.rows.filter(([, v]) => v != null && v !== '') }))
      .filter((x) => x.rows.length);
  }, [job, row, awb]);

  // ---- package details ----
  const pkg = useMemo(() => {
    if (!job) return null;
    const vol = job.volume || {};
    const facts = [
      ['Weight', job.consolidatedWeight != null ? `${job.consolidatedWeight} kg` : ''],
      // volume_value is the literal placeholder "1 TC" on every row, so it is shown only if real.
      ['Volume', vol.value && String(vol.value) !== '1' ? `${vol.value} ${vol.unit || ''}`.trim() : ''],
      ['Master AWB', job.masterAwbNumber],
      ['Ambient', job.ambient],
      ['Chilled', job.chilled],
      ['Frozen', job.frozen],
      ['Scanned piece', job.scannedChildAwb],
    ].filter(([, v]) => v != null && v !== '');
    // Drop placeholder line items: some orders carry a single row whose "name" is just the AWB,
    // which tells the reader nothing and only makes the card look populated.
    const items = (Array.isArray(job.lineItems) ? job.lineItems : []).filter(
      (it) => it && it.name && it.name !== job.jobId && it.name !== awb
    );
    return { facts, items, children: Array.isArray(job.childAwbs) ? job.childAwbs : [] };
  }, [job, awb]);

  const emptyState = (icon, text) => (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
      <i className={`fa ${icon}`} style={{ fontSize: 40, opacity: 0.3, display: 'block', marginBottom: 10 }} />
      {text}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Poppins', sans-serif" }}>
      <style>{`
        @keyframes lodFade { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        section[id^='sec-'] { scroll-margin-top: 90px; }
        .lod-tab { border:none; cursor:pointer; padding:9px 18px; font-size:13px; font-weight:600; border-radius:10px; font-family:'Poppins',sans-serif; display:inline-flex; align-items:center; gap:7px; transition:all 0.15s; }
      `}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', padding: '18px 28px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={back}
              title="Back to Live View"
              style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 10, width: 38, height: 38, cursor: 'pointer', color: '#fff', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <i className="fa fa-arrow-left" />
            </button>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fa fa-barcode" style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Order Details</div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{awb || '—'}</div>
            </div>
          </div>
          {(row?.type || row?.status || timeline[0]?.status) && (
            <span style={{ background: headMeta.bg, color: headMeta.color, borderRadius: 20, padding: '6px 14px', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <i className={`fa ${headMeta.icon}`} style={{ fontSize: 11 }} /> {headMeta.label}
            </span>
          )}
        </div>

        {/* Jump links. The sections are all rendered below; these just scroll to one, so the
             page still has the four familiar entry points without hiding three of them. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TABS.map(([t, ic, label]) => (
            <a
              key={t}
              className="lod-tab"
              href={`#sec-${t}`}
              style={{ background: 'transparent', color: 'rgba(255,255,255,0.62)', textDecoration: 'none' }}
            >
              <i className={`fa ${ic}`} style={{ fontSize: 12 }} /> {label}
              {t === 'duties' && duties.length > 0 && (
                <span style={{ background: '#60a5fa', color: '#0f172a', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 700 }}>{duties.length}</span>
              )}
            </a>
          ))}
        </div>
      </div>

      {/* Body — summary panel + active tab */}
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '22px 28px 60px', display: 'grid', gridTemplateColumns: 'minmax(300px, 360px) 1fr', gap: 22, alignItems: 'start' }}>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ROUTE — the first question anyone opening a shipment asks is where it goes from and to.
              It was not on this page at all, because getJobById returns one leg. */}
          {(route.from.address || route.to.address || route.from.name || route.to.name) && (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '18px 20px', animation: 'lodFade 0.3s ease-out both' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 }}>Route</div>
              {[
                { key: 'from', tag: 'PICK UP', colour: '#0f766e', bg: '#ecfdf5', d: route.from },
                { key: 'to', tag: 'DELIVER', colour: '#1d4ed8', bg: '#eff6ff', d: route.to },
              ].map(({ key, tag, colour, bg, d }, i) => (
                <div key={key} style={{ display: 'flex', gap: 12, paddingBottom: i === 0 ? 14 : 0, marginBottom: i === 0 ? 14 : 0, borderBottom: i === 0 ? '1px dashed #e8edf3' : 'none' }}>
                  <div style={{ flexShrink: 0, width: 62 }}>
                    <span style={{ display: 'inline-block', background: bg, color: colour, fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em', padding: '3px 7px', borderRadius: 5 }}>{tag}</span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>{d.name || '—'}</div>
                    {d.address && <div style={{ fontSize: 12.5, color: '#475569', marginTop: 2, lineHeight: 1.45 }}>{d.address}</div>}
                    {d.phone && (
                      <a href={`tel:${d.phone}`} style={{ fontSize: 12, color: colour, textDecoration: 'none', marginTop: 3, display: 'inline-block', fontWeight: 600 }}>{d.phone}</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* FORWARDED TO — an order handed to an external carrier is no longer ours to move, so
              who has it belongs next to the route, not buried in a tab. */}
          {(fwd || fwdError) && (
            <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${fwd ? '#ddd6fe' : '#e8edf3'}`, padding: '16px 20px', animation: 'lodFade 0.3s ease-out both' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Forwarded</div>
              {fwd ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ background: '#ede9fe', color: '#5b21b6', fontSize: 12.5, fontWeight: 800, padding: '5px 11px', borderRadius: 7 }}>
                      {fwd.carrierCode || fwd.carrierName || 'External carrier'}
                    </span>
                    {fwd.status && (
                      <span style={{ background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 700, padding: '4px 9px', borderRadius: 6 }}>
                        {String(fwd.status).replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px 14px' }}>
                    {[
                      ['Carrier HAWB', fwd.carrierHawb],
                      ['Partner', fwd.partnerName],
                      ['Movement', fwd.movementType],
                      ['Origin country', fwd.originCountry],
                      ['Attempts', fwd.attemptCount != null && fwd.maxAttempts != null ? `${fwd.attemptCount} / ${fwd.maxAttempts}` : ''],
                      ['Last error', fwd.lastErrorMsg],
                      ['POD receiver', fwd.podReceiverName],
                      ['Carrier status', fwd.lastRemoteStatus],
                      ['Reason', fwd.lastRemoteReason || fwd.holdReason],
                      ['Mode', fwd.mode],
                      ['Direction', fwd.direction],
                      ['Service country', fwd.serviceCountry],
                      ['Pieces', fwd.pieceCount],
                      ['COD', fwd.codAmount != null ? `${fwd.codAmount} ${fwd.codCurrency || ''}`.trim() : ''],
                      ['Forwarded at', fmtDate(fwd.forwardedAt)],
                    ]
                      .filter(([, v]) => v != null && v !== '')
                      .map(([label, value]) => (
                        <div key={label}>
                          <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
                        </div>
                      ))}
                  </div>
                </>
              ) : (
                // Not "this order is not forwarded" - we genuinely could not ask. Saying which is
                // the difference between a fact and a guess.
                <div style={{ fontSize: 12.5, color: '#94a3b8', lineHeight: 1.5 }}>
                  Forwarding status could not be checked.
                  <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 3 }}>{fwdError}</div>
                </div>
              )}
            </div>
          )}

          {/* Grouped detail — sections rather than one long column of labels. */}
          {groups.map((g) => (
            <div key={g.title} style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '16px 20px', animation: 'lodFade 0.3s ease-out both' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>{g.title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px 14px' }}>
                {g.rows.map(([label, value]) => (
                  <div key={label} style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Package */}
          {pkg && (pkg.facts.length > 0 || pkg.items.length > 0 || pkg.children.length > 0) && (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '16px 20px', animation: 'lodFade 0.3s ease-out both' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Package</div>
              {pkg.facts.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '12px 14px', marginBottom: pkg.items.length || pkg.children.length ? 14 : 0 }}>
                  {pkg.facts.map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{value}</div>
                    </div>
                  ))}
                </div>
              )}
              {pkg.items.length > 0 && (
                <div style={{ marginBottom: pkg.children.length ? 14 : 0 }}>
                  <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 6 }}>Contents</div>
                  {pkg.items.map((it, i) => (
                    <div key={i} style={{ fontSize: 12.5, color: '#1e293b', padding: '5px 0', borderBottom: i < pkg.items.length - 1 ? '1px dashed #eef2f7' : 'none' }}>
                      <strong>{it.quantity != null ? `${it.quantity} × ` : ''}{it.name}</strong>
                      {it.quantityUnit ? <span style={{ color: '#94a3b8' }}> ({it.quantityUnit})</span> : null}
                    </div>
                  ))}
                </div>
              )}
              {pkg.children.length > 0 && (
                <div>
                  <div style={{ fontSize: 10.5, color: '#94a3b8', marginBottom: 6 }}>Pieces ({pkg.children.length})</div>
                  {pkg.children.map((c, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#1e293b', padding: '6px 0', borderBottom: i < pkg.children.length - 1 ? '1px dashed #eef2f7' : 'none' }}>
                      <div style={{ fontWeight: 700 }}>{c.childAwb || c.pieceReference || `Piece ${i + 1}`}</div>
                      <div style={{ color: '#64748b', fontSize: 11 }}>
                        {[c.description, c.weight != null ? `${c.weight} kg` : null, c.status].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Every section, stacked.
             These were four tabs, so reading one order meant clicking through four panels and
             remembering what the last one said. They are sections now - basic details, then proof
             of delivery, then the history, then the money - all on one scrollable page. The data
             was already fetched together in one Promise.allSettled, so showing it together costs
             nothing extra. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {loading ? (
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
              <i className="fa fa-spinner fa-spin" style={{ fontSize: 30, display: 'block', marginBottom: 10 }} />
              Loading…
            </div>
          ) : (
            <>
              <section id="sec-pod" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '20px 24px', animation: 'lodFade 0.3s ease-out both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <i className="fa fa-image" style={{ color: '#94a3b8', fontSize: 13 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Proof of Delivery</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{podImages.length + podSignatures.length || 0}</span>
                </div>
                {podImages.length === 0 && podSignatures.length === 0 ? (
            emptyState('fa-image', 'No POD images available')
          ) : (
            <>
              {podImages.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Delivery Images</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
                    {podImages.map((img, i) => (
                      <a key={i} href={img} target="_blank" rel="noreferrer">
                        <img src={img} alt={`pod-${i}`} style={{ width: '100%', borderRadius: 10, border: '1px solid #e2e8f0', objectFit: 'cover', height: 170 }} />
                      </a>
                    ))}
                  </div>
                </>
              )}
              {podSignatures.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Signatures</div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {podSignatures.map((sig, i) => (
                      <a key={i} href={sig} target="_blank" rel="noreferrer">
                        <img src={sig} alt={`sig-${i}`} style={{ height: 100, borderRadius: 10, border: '1px solid #e2e8f0' }} />
                      </a>
                    ))}
                  </div>
                </>
              )}
            </>
                )}
              </section>
              <section id="sec-timeline" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '20px 24px', animation: 'lodFade 0.3s ease-out both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <i className="fa fa-list-ul" style={{ color: '#94a3b8', fontSize: 13 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timeline</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{timeline.length || 0}</span>
                </div>
                {
            timeline.length === 0 ? (
              emptyState('fa-inbox', 'No timeline data found')
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 18, top: 4, bottom: 4, width: 2, background: '#e2e8f0' }} />
                {timeline.map((d, i) => {
                  const m = statusMeta(d.status);
                  return (
                    <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 18, position: 'relative', animation: `lodFade 0.3s ${i * 0.04}s ease-out both` }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: m.bg, border: `2px solid ${m.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                        <i className={`fa ${m.icon}`} style={{ fontSize: 13, color: m.color }} />
                      </div>
                      <div style={{ flex: 1, background: '#f8fafc', borderRadius: 12, padding: '12px 16px', border: `1px solid ${m.color}22` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 10, flexWrap: 'wrap' }}>
                          <span style={{ background: m.bg, color: m.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <i className={`fa ${m.icon}`} style={{ fontSize: 10 }} /> {d.status}
                          </span>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>{d.date}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {d.driverId && <span><strong>Driver:</strong> {driverName(d.driverId)}</span>}
                          {d.updatedBy && <span><strong>Updated By:</strong> {d.updatedBy}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </section>
              <section id="sec-duties" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '20px 24px', animation: 'lodFade 0.3s ease-out both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <i className="fa fa-file-text-o" style={{ color: '#94a3b8', fontSize: 13 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Duties & Tax</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{duties.length || 0}</span>
                </div>
                {
            duties.length === 0 ? (
              emptyState('fa-file-text-o', 'No duties & tax for this AWB')
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#64748b' }}>
                    <th style={{ padding: '8px 6px' }}>Charge</th>
                    <th style={{ padding: '8px 6px', textAlign: 'right' }}>Amount</th>
                    <th style={{ padding: '8px 6px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {duties.map((d, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #eef2f7' }}>
                      <td style={{ padding: '8px 6px', fontWeight: 600 }}>{d.name}</td>
                      <td style={{ padding: '8px 6px', textAlign: 'right' }}>{Number(d.amount || 0).toFixed(2)} {d.currency || 'AED'}</td>
                      <td style={{ padding: '8px 6px' }}>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: d.status === 'COLLECTED' ? '#dcfce7' : '#fef9c3', color: d.status === 'COLLECTED' ? '#166534' : '#854d0e' }}>
                          {d.status === 'COLLECTED' ? 'Collected' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700 }}>
                    <td style={{ padding: '8px 6px' }}>Total</td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>{dutiesTotal.toFixed(2)} AED</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            )}
              </section>
              <section id="sec-custody" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8edf3', padding: '20px 24px', animation: 'lodFade 0.3s ease-out both' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' }}>
                  <i className="fa fa-exchange" style={{ color: '#94a3b8', fontSize: 13 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em' }}>COD Custody</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{custody ? 1 : 0 || 0}</span>
                </div>
                {
            !custody ? (
              emptyState('fa-exchange', 'No COD custody record for this AWB')
            ) : (
              <div style={{ fontSize: 13, color: '#334155' }}>
                <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 18 }}>
                  <div><div style={{ fontSize: 11, color: '#94a3b8' }}>Current stage</div><div style={{ fontWeight: 700 }}>{custody.stage || '—'}</div></div>
                  <div><div style={{ fontSize: 11, color: '#94a3b8' }}>Amount</div><div style={{ fontWeight: 700 }}>{Number(custody.amount || 0).toFixed(2)} {custody.currency || 'AED'}</div></div>
                  <div><div style={{ fontSize: 11, color: '#94a3b8' }}>COD</div><div style={{ fontWeight: 700 }}>{Number(custody.codAmount || 0).toFixed(2)}</div></div>
                  <div><div style={{ fontSize: 11, color: '#94a3b8' }}>Duties/Tax</div><div style={{ fontWeight: 700 }}>{Number(custody.dutiesTaxAmount || 0).toFixed(2)}</div></div>
                </div>
                {(custody.trail || custody.events || []).length > 0 && (
                  <div style={{ paddingLeft: 6 }}>
                    {(custody.trail || custody.events || []).map((e, i) => (
                      <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#3b82f6', marginTop: 4 }} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{e.stage || e.toStage || e.action || '—'}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{e.holderName || e.byName || e.by || ''} {e.at || e.createdAt || e.time || ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
